import React, { useEffect, useState } from 'react';
import api from '../../config/axios';
import { groupTotal } from '../../utils/tax';
import { useNavigate, useLocation } from 'react-router-dom';
import '../order/OrderCreate.css';

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  description?: string;
  imageUrl: string;
}

interface OrderItem {
  menuId: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl: string;
}

interface GroupOrder {
  sessionCode: string;
  tableId: string;
  tableName: string;
  status: string;
  orders: Array<{
    orderId: string;
    customerName: string;
    customerPhone: string;
    items: OrderItem[];
    subtotal: number;
    createdAt: string;
  }>;
  totalAmount: number;
  expiresAt: string;
  createdAt: string;
}

const GroupOrder: React.FC = () => {
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuLoading, setMenuLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [sessionCode, setSessionCode] = useState('');
  const [groupOrder, setGroupOrder] = useState<GroupOrder | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('sessionCode');
    if (code) {
      setSessionCode(code);
      fetchGroupSession(code);
    }
  }, [location.search]);

  useEffect(() => {
    if (sessionCode) {
      const interval = setInterval(() => {
        fetchGroupSession(sessionCode);
      }, 10000);
      setRefreshInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [sessionCode]);

  const fetchGroupSession = async (code: string) => {
    try {
      console.log('Fetching group session:', code);
      const res = await api.get(`/api/group-orders/${code}`);
      console.log('Group session response:', res.data);
      console.log('Group session data:', res.data.data);
      console.log('Group session status:', res.data.data?.status);
      setGroupOrder(res.data.data);
    } catch (err: any) {
      console.error('Error fetching group session:', err);
      if (err.response?.status === 404 || err.response?.status === 400) {
        setError('Session đã hết hạn hoặc không tồn tại');
        setTimeout(() => {
          navigate('/orders/group-join');
        }, 2000);
      }
    }
  };

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setMenuLoading(true);
        const res = await api.get('/api/menu');
        const menuData = res.data.data || res.data;
        setMenu(menuData);
      } catch (err) {
        setMenu([]);
      } finally {
        setMenuLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const handleAddItem = (item: MenuItem) => {
    console.log('Adding item:', item);
    setOrderItems(prev => {
      const found = prev.find(i => i.menuId === item._id);
      if (found) {
        const newItems = prev.map(i => i.menuId === item._id ? { ...i, quantity: i.quantity + 1 } : i);
        console.log('Updated items:', newItems);
        return newItems;
      } else {
        const newItems = [...prev, { menuId: item._id, name: item.name, price: item.price, quantity: 1, imageUrl: item.imageUrl }];
        console.log('Added new item:', newItems);
        return newItems;
      }
    });
  };

  const handleChangeQty = (menuId: string, qty: number) => {
    setOrderItems(prev => prev.map(i => i.menuId === menuId ? { ...i, quantity: qty } : i));
  };

  const handleRemoveItem = (menuId: string) => {
    setOrderItems(prev => prev.filter(i => i.menuId !== menuId));
  };

  const subtotal = orderItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const phoneRegex = /^(0|\+84)(3[2-9]|5[689]|7[06-9]|8[1-689]|9[0-46-9])[0-9]{7}$/;
    return phoneRegex.test(cleanPhone);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerPhone(value);
    
    if (value.trim() === '') {
      setPhoneError('');
    } else if (!validatePhone(value)) {
      setPhoneError('Số điện thoại không hợp lệ');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!customerName.trim() || !customerPhone.trim()) {
      setError('Vui lòng nhập tên và số điện thoại!');
      return;
    }
    if (!validatePhone(customerPhone)) {
      setError('Số điện thoại không hợp lệ!');
      return;
    }
    if (orderItems.length === 0) {
      setError('Vui lòng chọn ít nhất 1 món!');
      return;
    }
    if (!sessionCode) {
      setError('Không xác định được session!');
      return;
    }
    
    try {
      setLoading(true);
      const res = await api.post('/api/group-orders/add-order', {
        sessionCode,
        customerName,
        customerPhone,
        items: orderItems
      });
      
      setSuccess('✅ Đã thêm món vào nhóm thành công!');
      setOrderItems([]);
      setCustomerName('');
      setCustomerPhone('');
      
      fetchGroupSession(sessionCode);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi thêm món vào nhóm');
    } finally {
      setLoading(false);
    }
  };

  const handlePayGroup = () => {
    if (!groupOrder || groupOrder.orders.length === 0) {
      setError('Không có đơn hàng nào để thanh toán');
      return;
    }
    
    if (groupOrder.status === 'pending_payment') {
      setError('Đơn hàng đang chờ thanh toán MoMo. Vui lòng hoàn tất thanh toán trước.');
      return;
    }
    
    // Thuế theo từng món, cộng dồn cả phiên.
    const totalWithTax = Math.round(groupTotal(groupOrder));
    navigate(`/orders/group-payment?sessionCode=${sessionCode}&total=${totalWithTax}`);
  };

  const handleCancelSession = async () => {
    if (!confirm('Bạn có chắc muốn hủy session nhóm này?')) return;
    
    try {
      await api.delete(`/api/group-orders/${sessionCode}`);
      setSuccess('Đã hủy session nhóm');
      setTimeout(() => {
        navigate('/orders/group-join');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi hủy session');
    }
  };

  if (!groupOrder) {
    return (
      <div className="order-create-bg">
        <div className="order-create-container">
          <div className="order-create-loading">Đang tải thông tin nhóm...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-create-bg">
      <div className="order-create-container">
        <h2 className="order-create-title">ĐẶT MÓN NHÓM</h2>
        
        <div style={{ 
          background: groupOrder.status === 'pending_payment' ? '#fff7ed' : '#f0f9ff', 
          border: groupOrder.status === 'pending_payment' ? '1px solid #f59e0b' : '1px solid #0ea5e9', 
          borderRadius: 8, 
          padding: 16, 
          marginBottom: 24 
        }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Session Code: <span style={{ color: '#0ea5e9', fontSize: '18px' }}>{groupOrder.sessionCode}</span></strong>
            {groupOrder.status === 'active' && (
              <button 
                onClick={handleCancelSession}
                className="order-create-remove-btn"
                style={{ padding: '8px 12px', fontSize: '12px' }}
              >
                Hủy Session
              </button>
            )}
          </div>
          <div>Bàn: {groupOrder.tableName}</div>
          <div>Thời gian hết hạn: {new Date(groupOrder.expiresAt).toLocaleString()}</div>
          <div style={{ marginTop: 8, fontWeight: 'bold', color: '#059669' }}>
            Tổng tiền nhóm: {groupOrder.totalAmount.toLocaleString()} đ
          </div>
          {groupOrder.status === 'pending_payment' && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: '#fef3c7', borderRadius: 4, color: '#92400e' }}>
              ⏳ Đang chờ thanh toán MoMo...
            </div>
          )}
        </div>

        {error && <div className="order-create-message order-create-error">{error}</div>}
        {success && <div className="order-create-message order-create-success">{success}</div>}
        
        {!groupOrder && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            background: '#f8fafc', 
            borderRadius: 8,
            marginBottom: 16
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Đang tải thông tin session...</div>
          </div>
        )}
        
        {groupOrder && (
          <div className="order-create-main">
            <div className="order-create-left">
            <h3 className="order-create-section-title">🍽️ Menu</h3>
            {menuLoading ? (
              <div className="order-create-empty">
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                <div>Đang tải menu...</div>
              </div>
            ) : menu.length === 0 ? (
              <div className="order-create-empty">
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>😔</div>
                <div>Không có món nào trong menu</div>

              </div>
            ) : (
              <div className="order-create-menu-list">
                {menu.map(item => (
                  <div key={item._id} className="order-create-menu-item">
                    <img 
                      src={item.imageUrl} 
                      alt={item.name} 
                      className="order-create-menu-img"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/80x80?text=🍽️';
                      }}
                    />
                    <div className="order-create-menu-info">
                      <div className="order-create-menu-name">{item.name}</div>
                      {item.description && (
                        <div className="order-create-menu-description" style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                          {item.description}
                        </div>
                      )}
                      <div className="order-create-menu-price">{item.price.toLocaleString()} đ</div>
                    </div>
                                          <button 
                        onClick={() => handleAddItem(item)} 
                        className="order-create-menu-add-btn"
                        disabled={groupOrder.status !== 'active' || groupOrder.status === undefined}
                        style={{ 
                          background: (groupOrder.status !== 'active' || groupOrder.status === undefined) ? '#e5e7eb' : '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: (groupOrder.status !== 'active' || groupOrder.status === undefined) ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        {(groupOrder.status !== 'active' || groupOrder.status === undefined) ? '⏳ Không thể thêm' : '➕ Thêm'}
                      </button>
                  </div>
                ))}
              </div>
            )}
          </div>

            {groupOrder && (
              <div className="order-create-right">
                <h3 className="order-create-section-title">🛒 Đặt món của bạn</h3>
                
                {(groupOrder.status !== 'active' && groupOrder.status !== undefined) && (
                  <div style={{ 
                    background: groupOrder.status === 'pending_payment' ? '#fef3c7' : '#fee2e2', 
                    border: groupOrder.status === 'pending_payment' ? '1px solid #f59e0b' : '1px solid #ef4444', 
                    borderRadius: 8, 
                    padding: 12, 
                    marginBottom: 16,
                    fontSize: '14px',
                    color: groupOrder.status === 'pending_payment' ? '#92400e' : '#dc2626'
                  }}>
                    {groupOrder.status === 'pending_payment' ? 
                      '⚠️ Đang chờ thanh toán MoMo. Bạn vẫn có thể xem menu nhưng không thể đặt thêm món.' :
                      `⚠️ Session hiện tại có status: ${groupOrder.status}. Chỉ có thể đặt món khi status là 'active'.`
                    }
                  </div>
                )}
                
                {groupOrder.status === undefined && (
                  <div style={{ 
                    background: '#fef3c7', 
                    border: '1px solid #f59e0b', 
                    borderRadius: 8, 
                    padding: 12, 
                    marginBottom: 16,
                    fontSize: '14px',
                    color: '#92400e'
                  }}>
                    ⚠️ Đang tải thông tin session... Vui lòng chờ một chút.
                  </div>
                )}
                

                
                <div className="order-create-inputs-group">
                  <input
                    type="text"
                    placeholder="Tên của bạn"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                    className="order-create-input"
                    style={{ marginRight: 8 }}
                    disabled={groupOrder.status !== 'active' || groupOrder.status === undefined}
                  />
                  <input
                    type="tel"
                    placeholder="Số điện thoại"
                    value={customerPhone}
                    onChange={handlePhoneChange}
                    className={`order-create-input ${phoneError ? 'order-create-input-error' : ''}`}
                    disabled={groupOrder.status !== 'active' || groupOrder.status === undefined}
                  />
                </div>
                {phoneError && <div className="order-create-message order-create-error" style={{ fontSize: '12px', marginTop: '4px' }}>{phoneError}</div>}

                {orderItems.length === 0 ? (
                  <div className="order-create-empty">
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🍽️</div>
                    <div>Chưa có món nào được chọn</div>
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                      Hãy chọn món từ menu bên trái
                    </div>
                  </div>
                ) : (
                  <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Món đã chọn:</h4>
                    <table className="order-create-table">
                      <thead>
                        <tr>
                          <th>Món</th>
                          <th>Giá</th>
                          <th>Số lượng</th>
                          <th>Thành tiền</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map(item => (
                          <tr key={item.menuId}>
                            <td style={{ fontWeight: '500' }}>{item.name}</td>
                            <td>{item.price.toLocaleString()} đ</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button 
                                  onClick={() => handleChangeQty(item.menuId, Math.max(1, item.quantity - 1))}
                                  style={{ 
                                    background: '#e5e7eb', 
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    width: '24px', 
                                    height: '24px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  min={1}
                                  value={item.quantity}
                                  onChange={e => handleChangeQty(item.menuId, Math.max(1, Number(e.target.value)))}
                                  className="order-create-quantity-input"
                                  style={{ width: '50px', textAlign: 'center' }}
                                />
                                <button 
                                  onClick={() => handleChangeQty(item.menuId, item.quantity + 1)}
                                  style={{ 
                                    background: '#10b981', 
                                    color: 'white',
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    width: '24px', 
                                    height: '24px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            </td>
                            <td style={{ fontWeight: 'bold', color: '#059669' }}>
                              {(item.price * item.quantity).toLocaleString()} đ
                            </td>
                            <td>
                              <button 
                                onClick={() => handleRemoveItem(item.menuId)} 
                                className="order-create-remove-btn"
                                style={{ 
                                  background: '#ef4444', 
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer'
                                }}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={{ 
                  background: '#f0fdf4', 
                  border: '1px solid #22c55e', 
                  borderRadius: '8px', 
                  padding: '16px', 
                  marginTop: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669', marginBottom: '8px' }}>
                    Tổng cộng: {subtotal.toLocaleString()} đ
                  </div>
                  <button 
                    onClick={handleSubmit} 
                    className="order-create-submit-btn" 
                    disabled={loading || orderItems.length === 0 || groupOrder.status !== 'active' || groupOrder.status === undefined} 
                    style={{ 
                      width: '100%',
                      fontSize: '16px', 
                      padding: '16px',
                      background: (orderItems.length === 0 || groupOrder.status !== 'active' || groupOrder.status === undefined) ? '#e5e7eb' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (orderItems.length === 0 || groupOrder.status !== 'active' || groupOrder.status === undefined) ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {loading ? '⏳ Đang xử lý...' : 
                     (groupOrder.status !== 'active' || groupOrder.status === undefined) ? '⏳ Không thể thêm' :
                     orderItems.length === 0 ? 'Chọn món trước' : '✅ Thêm vào nhóm'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {groupOrder && groupOrder.status === 'pending_payment' && (
          <div style={{ 
            background: '#fff7ed', 
            border: '1px solid #f59e0b', 
            borderRadius: 8, 
            padding: 24, 
            marginBottom: 24,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', marginBottom: 16 }}>⏳</div>
            <h3 style={{ marginBottom: 12, color: '#92400e' }}>Đang chờ thanh toán MoMo</h3>
            <p style={{ color: '#92400e', marginBottom: 16 }}>
              Đơn hàng đã được tạo và đang chờ thanh toán qua MoMo. 
              Vui lòng hoàn tất thanh toán để tiếp tục.
            </p>
            <div style={{ fontSize: '14px', color: '#92400e' }}>
              Không thể đặt thêm món trong khi đang chờ thanh toán.
            </div>
          </div>
        )}

        {groupOrder && (
          <div style={{ marginTop: 36 }}>
            <h3 className="order-create-section-title">
              Đơn hàng trong nhóm ({groupOrder.orders.length} người)
            </h3>
          
          {groupOrder.orders.length === 0 ? (
            <div className="order-create-empty">Chưa có ai đặt món.</div>
          ) : (
            <div>
              {groupOrder.orders.map((order, index) => (
                <div key={order.orderId} style={{ 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 8, 
                  padding: 16, 
                  marginBottom: 16,
                  background: '#f8fafc'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>{order.customerName}</strong>
                    <span style={{ color: '#059669', fontWeight: 'bold' }}>
                      {order.subtotal.toLocaleString()} đ
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 8 }}>
                    {order.customerPhone} • {new Date(order.createdAt).toLocaleTimeString()}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {order.items.map((item, idx) => (
                      <span key={idx} style={{ marginRight: 12 }}>
                        {item.name} x{item.quantity}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              
              <div style={{ 
                background: '#f0fdf4', 
                border: '1px solid #22c55e', 
                borderRadius: 8, 
                padding: 16, 
                marginTop: 16,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669', marginBottom: 8 }}>
                  Tổng tiền nhóm: {groupOrder.totalAmount.toLocaleString()} đ
                </div>
                <div style={{ fontSize: '14px', color: '#059669', marginBottom: 16 }}>
                  (Chưa bao gồm thuế 10%)
                </div>
                <button 
                  onClick={handlePayGroup}
                  className="order-create-submit-btn"
                  style={{ fontSize: '16px', padding: '12px 24px' }}
                  disabled={groupOrder.status === 'pending_payment'}
                >
                  {groupOrder.status === 'pending_payment' ? 'Đang Chờ Thanh Toán...' : 'Thanh Toán Gộp'}
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default GroupOrder; 